<!--
    REV-21370 — the closing-wide Korrekturbuchungen register (Pure View), ADR-0015.

    Layout, ordering and German labels follow the AWF prototype's `Korrekturbuchungen` tab. Where the
    prototype and the ADR disagree the ADR wins, and each such place is named at the spot:

      - THREE status values, not the prototype's two (§2 — see `abcp-adjustment-status`);
      - NO system name in any label (§3a — it comes from the mandate's connection);
      - the ambiguity marking, which the prototype has no notion of (§4).

    Every `data-testid` is contract; class names are styling and may be renamed.
-->
<div class="adj" data-testid="closing-adjustments">
    <!-- ── The control bar: pills · GJ selector · Neue Buchung · Übertragen (n) ─────────────── -->
    <div class="adj-bar">
        <!--
            The three pills, ALWAYS all three — never hidden at count 0 (AC3). Absent in a prior year,
            which is fully read-only (§10): there is nothing to filter for when there is no work.
        -->
        <div class="adj-pills" data-testid="adjustment-filters" *ngIf="!readOnly">
            <ham-filter-pill
                data-testid="adjustment-filter"
                *ngFor="let key of filterKeys; trackBy: trackBySelf"
                [attr.data-filter]="key"
                [label]="filterLabelKey(key) | i18next"
                [count]="filterCount(key)"
                [active]="key === activeFilter"
                (pillClick)="onFilterClick(key)"
            ></ham-filter-pill>
        </div>

        <div class="adj-bar__spacer"></div>


        <!-- No entry and no transfer in a prior year (§10). -->

        <!--
            ⚠️ **Order matters** (prototype, 03.09.2026): `Neue Korrekturbuchung`, then the
            transfer control, then the year selector — left to right, that is the order of the
            work: write one, hand them over, then look at a different year. The year selector
            changes what the WHOLE table shows, so it belongs at the end rather than wedged
            between the two actions, which is where it used to sit.
        -->
        <!-- The fiscal-year selector: the current year and the two before it (§10). -->
        <div class="adj-year">
            <span class="adj-year__label">{{ 'abcp_adjustments_fiscal_year' | i18next }}</span>
            <ham-menu-button
                data-testid="adjustment-year"
                menuTestId="adjustment-year-menu"
                itemTestId="adjustment-year-option"
                [items]="yearItems"
                [menuWidth]="200"
                (itemSelect)="onFiscalYearSelect($event)"
            >
                {{ selectedFiscalYear !== null ? yearLabel(selectedFiscalYear) : '' }}
            </ham-menu-button>
        </div>
    </div>

    <!--
        ⚠️ **A SECOND row, above the table** (prototype 03.09.2026): the two ACTIONS live here,
        RIGHT-aligned (REV-22096), with the transfer first and `Neue Korrekturbuchung` at the trailing
        edge — where the document hub keeps `Hochladen`. The row above holds what SELECTS — the filter
        pills on the left, the year on the right — and mixing a control that changes the whole table in
        among two buttons that write to it made the bar read as one undifferentiated strip of controls.
    -->
    <div class="adj-actions" *ngIf="!readOnly">
    
            <!--
                ⚠️ **The label SAYS "all released"** (V2/AC5), because that is what the control does:
                there is no row selection in this surface, so the action takes every released
                correction of the year. A button reading only «Übertragen» beside a list that also
                shows transferred and booked rows invites the reading "transfer what I am looking
                at" — and a bulk action whose scope the user has to infer is how a bulk release
                happens by accident. The transfer itself releases nothing; it only ever acts on
                what a person has already released.

                The three targets each carry the REASON they exist (AC7). `Direkt ans ERP senden` is
                enabled only where the mandate's connected system can write — a QUERIED capability,
                never a constant (§3a rule 3) — and its tooltip then says why not. The system name
                comes from the data; this template never names one.
            -->
            <ham-menu-button
                data-testid="adjustment-transfer"
                menuTestId="adjustment-transfer-menu"
                itemTestId="adjustment-transfer-target"
                tooltipClass="abcp"
                icon="assets/hamIcons/upload.svg"
                [items]="transferItems"
                [notice]="transferNotice"
                [disabled]="!canTransfer"
                (itemSelect)="onTransferTargetClick($event)"
            >
                {{ 'abcp_adjustments_transfer_all_released' | i18next
                }}<span *ngIf="openCount > 0">&nbsp;({{ openCount }})</span>
            </ham-menu-button>
            <!-- `Neue Korrekturbuchung` — the primary action, at the trailing edge. -->
            <ham-button
                data-testid="adjustment-create"
                variant="primary"
                size="toolbar"
                icon="assets/hamIcons/plus-outline.svg"
                *ngIf="!readOnly"
                (buttonClick)="onCreateClick()"
            >
                {{ 'abcp_adjustments_new' | i18next }}
            </ham-button>
    </div>

    <!--
        The search hit count (AC3). A SEPARATE line from the empty state, because "no hit" and "nothing
        here" are different facts and are worded differently.
    -->
    <p class="adj-hits" data-testid="adjustment-search-hits" *ngIf="isSearching && searchHits !== null">
        {{ 'abcp_adjustments_search_hits' | i18next: { count: searchHits, term: searchTerm } }}
        <button type="button" class="adj-hits__reset" data-testid="adjustment-search-reset" (click)="onSearchResetClick()">
            {{ 'abcp_adjustments_search_reset' | i18next }}
        </button>
    </p>

    <!-- ── The table ───────────────────────────────────────────────────────────────────────── -->
    <div class="adj-scroll">
        <table class="adj-table" data-testid="adjustment-table">
            <thead>
                <tr class="adj-head">
                    <th
                        class="adj-head__cell"
                        data-testid="adjustment-column"
                        scope="col"
                        *ngFor="let column of columns; trackBy: trackBySelf"
                        [attr.data-column]="column"
                        [attr.aria-sort]="ariaSort(column)"
                    >
                        <abcp-sort-header
                            [direction]="sortDirection(column)"
                            [align]="headerAlign(column)"
                            (sortClick)="onSortClick(column)"
                        >
                            {{ columnLabelKey(column) | i18next }}
                        </abcp-sort-header>
                    </th>
                </tr>
            </thead>

            <tbody>
                <!-- Loading: grey placeholders in the SHAPE of the list, so the height does not jump (AC11). -->
                <tr *ngIf="showSkeleton">
                    <td class="adj-state" [attr.colspan]="columnCount">
                        <div class="adj-loading" data-testid="adjustment-loading">
                            <div class="adj-skeleton" *ngFor="let row of skeletonRows; trackBy: trackBySelf" aria-hidden="true"></div>
                        </div>
                    </td>
                </tr>

                <!-- Error: a short note WITH a way to try again; the pills and the year stay usable (AC11). -->
                <tr *ngIf="!loading && error">
                    <td class="adj-state" [attr.colspan]="columnCount">
                        <div class="adj-error" data-testid="adjustment-error">
                            <span>{{ 'abcp_adjustments_error' | i18next }}</span>
                            <button type="button" class="adj-error__retry" data-testid="adjustment-retry" (click)="onRetryClick()">
                                {{ 'abcp_adjustments_retry' | i18next }}
                            </button>
                        </div>
                    </td>
                </tr>

                <ng-container *ngIf="!showSkeleton && !error">
                    <!--
                        `Provisorisches Jahresergebnis · vor Korrekturbuchungen` — ABOVE the rows, as the
                        prototype has it, and absent in a prior year (there is no provisional result for a
                        year that is closed). Computed upstream (§11).
                    -->
                    <tr class="adj-row adj-row--result" data-testid="adjustment-result-before" *ngIf="!readOnly && result">
                        <td class="adj-cell adj-cell--result-label" colspan="3">
                            {{ 'abcp_adjustments_result_provisional' | i18next }}
                            <span class="adj-cell__muted">{{ 'abcp_adjustments_result_provisional_hint' | i18next }}</span>
                        </td>
                        <td class="adj-cell adj-cell--value adj-cell--strong" data-testid="adjustment-result-before-amount">
                            {{ result.currency }}&nbsp;{{ result.provisionalMinor / 100 | finNumber: '1.2-2' }}
                        </td>
                        <td class="adj-cell" colspan="3"></td>
                    </tr>

                    <!-- Empty · and "no search hit" is a DIFFERENT sentence (AC11). -->
                    <tr *ngIf="isEmpty">
                        <td class="adj-state" [attr.colspan]="columnCount">
                            <div class="adj-empty" data-testid="adjustment-no-hits" *ngIf="isSearching">
                                {{ 'abcp_adjustments_no_hits' | i18next: { term: searchTerm } }}
                            </div>
                            <div class="adj-empty" data-testid="adjustment-empty" *ngIf="!isSearching">
                                <ng-container *ngIf="readOnly">{{
                                    'abcp_adjustments_empty_year' | i18next: { year: selectedFiscalYear }
                                }}</ng-container>
                                <ng-container *ngIf="!readOnly">{{ 'abcp_adjustments_empty' | i18next }}</ng-container>
                            </div>
                        </td>
                    </tr>

                    <tr
                        class="adj-row adj-row--booking"
                        data-testid="adjustment-row"
                        *ngFor="let row of adjustments; trackBy: trackById"
                        [attr.data-adjustment-id]="row.id"
                        [attr.data-state]="row.state"
                        [attr.data-source]="row.source"
                        tabindex="0"
                        (click)="onRowClick(row)"
                        (keydown.enter)="onRowClick(row)"
                        (keydown.space)="onRowClick(row); $event.preventDefault()"
                    >
                        <td class="adj-cell adj-cell--text" data-testid="adjustment-cell" data-column="text">{{ row.text }}</td>
                <!--
                    `Soll` / `Haben` — the NUMBER and the NAME (prototype change 03.09.2026).
                    A bare number is only readable to someone who knows the chart by heart, and the
                    whole point of the column is telling the reader which account this hits. The name
                    truncates with the full value in the tooltip, like every other narrow cell here.
                -->
                        <td class="adj-cell adj-cell--account" data-testid="adjustment-cell" data-column="debit">
                            <code>{{ row.debit.number }}</code>
                            <span
                                class="adj-account__name"
                                [hamTooltip]="row.debit.name"
                                tooltipClass="abcp"
                                [tooltipOnlyWhenTruncated]="true"
                                >{{ row.debit.name }}</span
                            >
                        </td>
                        <td class="adj-cell adj-cell--account" data-testid="adjustment-cell" data-column="credit">
                            <code>{{ row.credit.number }}</code>
                            <span
                                class="adj-account__name"
                                [hamTooltip]="row.credit.name"
                                tooltipClass="abcp"
                                [tooltipOnlyWhenTruncated]="true"
                                >{{ row.credit.name }}</span
                            >
                        </td>
                        <td class="adj-cell adj-cell--value" data-testid="adjustment-cell" data-column="amount">
                            {{ row.amount.currency }}&nbsp;{{ row.amount.minor / 100 | finNumber: '1.2-2' }}
                        </td>
                        <!--
                            `P&L` — what this one correction does to the year result.
                            Read from the SERVER's `resultEffect`, never derived here: relevance comes
                            from the mandate's account group (Q10) and the sign from the posting side,
                            and a second copy of that rule in a template is how a column and the
                            `Einfluss` total below it come to disagree.
                        -->
                        <td class="adj-cell adj-cell--pnl" data-testid="adjustment-cell" data-column="pnl">
                            <!--
                                The DIRECTION only: + raises the year result, − lowers it, — leaves
                                it alone. Not the amount -- that is already the `Betrag` column two
                                cells left, and repeating it here says nothing new. The words stay in
                                the tooltip so the cell is not sign-only for a screen reader, and —
                                means "no effect", which is a different statement from 0.00.
                            -->
                            <span
                                class="adj-pnl"
                                [attr.data-effect]="row.resultEffect"
                                [hamTooltip]="resultEffectKey(row.resultEffect) | i18next"
                                tooltipClass="abcp"
                            >
                                {{ resultEffectSign(row.resultEffect) }}
                            </span>
                        </td>
                        <!--
                            `Quelle` (§8). Today `Manuell` throughout the current year and `VJ` on a
                            prior-year row. The column is the connection point for a later `Agent` variant —
                            and it must NOT suggest a proposal flow that does not exist: no confidence, no
                            reasoning, no accept, no reject.
                        -->
                        <td class="adj-cell adj-cell--source" data-testid="adjustment-cell" data-column="source">
                            <!--
                                `Quelle` — INITIALS for a person, the word only for the agent
                                (prototype change 03.09.2026). "User" as a label says nothing a reader
                                did not already assume; WHO stands behind the booking is the fact the
                                column is for. Full name in the tooltip.
                            -->
                            <span
                                class="adj-source"
                                [attr.data-source]="row.source"
                                [hamTooltip]="sourceTitle(row)"
                                tooltipClass="abcp"
                                >{{ sourceDisplay(row) | i18next
                            }}</span>
                        </td>
                        <td class="adj-cell adj-cell--state" data-testid="adjustment-cell" data-column="state">
                            <abcp-adjustment-status
                                [state]="row.state"
                                [bookedYear]="bookedYearOf(row)"
                                [ambiguous]="!!row.ambiguous"
                                [notFound]="!!row.notFound"
                            ></abcp-adjustment-status>
                        </td>

                        <!--
                            NO ROW MENU (prototype, 03.09.2026). Editing and discarding are done on the
                            DETAIL surface, which is one click away and shows the whole record while it
                            changes. A per-row menu offered the same two actions from a table row that
                            shows six of the fifteen fields — the user acted without seeing what they
                            were acting on, and the menu had to re-implement the `editable` rule the
                            detail surface already asks the backend for.
                        -->
                    </tr>

                    <!--
                        `Jahresergebnis nach Korrekturbuchungen`, with the signed `Einfluss
                        ergebniswirksamer Korrekturen` beside it (AC4). A negative result is visibly
                        negative — it is a figure, not an error.
                    -->
                    <tr
                        class="adj-row adj-row--result adj-row--result-after"
                        data-testid="adjustment-result-after"
                        [class.is-negative]="hasNegativeResult"
                        *ngIf="!readOnly && result"
                    >
                        <td class="adj-cell adj-cell--result-label" colspan="3">
                            {{ 'abcp_adjustments_result_after' | i18next }}
                            <span class="adj-cell__muted" data-testid="adjustment-result-impact">
                                {{ 'abcp_adjustments_result_impact' | i18next }}: {{ impactSign }}{{ result.currency }}&nbsp;{{
                                    impactMagnitudeMinor / 100 | finNumber: '1.2-2'
                                }}
                            </span>
                        </td>
                        <td
                            class="adj-cell adj-cell--value adj-cell--strong"
                            data-testid="adjustment-result-after-amount"
                            [class.is-negative]="hasNegativeResult"
                        >
                            {{ result.currency }}&nbsp;{{ result.resultAfterMinor / 100 | finNumber: '1.2-2' }}
                        </td>
                        <td class="adj-cell" colspan="3"></td>
                    </tr>
                </ng-container>
            </tbody>

            <!--
                `Total Korrekturbuchungen` — the sum of every listed amount (AC4). The table's last row, so
                its amount sits under `Betrag` like every other amount. In a prior year the row names the
                year instead, as the prototype does.
            -->
            <tfoot *ngIf="!showSkeleton && !error && result">
                <tr class="adj-total" data-testid="adjustment-total">
                    <td class="adj-total__label" colspan="3">
                        <ng-container *ngIf="readOnly">{{
                            'abcp_adjustments_total_year' | i18next: { year: selectedFiscalYear }
                        }}</ng-container>
                        <ng-container *ngIf="!readOnly">{{ 'abcp_adjustments_total' | i18next }}</ng-container>
                    </td>
                    <td class="adj-total__value" data-testid="adjustment-total-amount">
                        {{ result.currency }}&nbsp;{{ result.totalMinor / 100 | finNumber: '1.2-2' }}
                    </td>
                    <td colspan="3"></td>
                </tr>
            </tfoot>
        </table>
    </div>

    <!--
        The honesty note about the impact figure (§11 / Q10): an account that is not classified yet has
        no statement, so its correction is in the total and NOT in the impact. Said out loud rather than
        left as a silent shortfall.
    -->
    <p
        class="adj-note"
        data-testid="adjustment-unclassified-note"
        *ngIf="!showSkeleton && !error && result && result.unclassifiedCount"
    >
        {{ 'abcp_adjustments_unclassified_note' | i18next: { count: result.unclassifiedCount } }}
    </p>
</div>
